"use client"

import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { BarChart3, TrendingUp, TrendingDown, DollarSign, Clock, Activity, CheckCircle2, XCircle } from "lucide-react"
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

export function SimulationAnalytics() {
  const [dateRange, setDateRange] = useState("30d")
  const [selectedModel, setSelectedModel] = useState("all")
  const [selectedSource, setSelectedSource] = useState("all")
  const [selectedOwner, setSelectedOwner] = useState("all")
  const [selectedCallStatus, setSelectedCallStatus] = useState("all")

  const [costPer1kTokens, setCostPer1kTokens] = useState("0.02")
  const [includeSimulationCalls, setIncludeSimulationCalls] = useState(true)

  const [usageData, setUsageData] = useState({
    totalInputTokens: 2340120,
    totalOutputTokens: 2240114,
    totalTokens: 4580234,
    totalCost: 0,
  })

  const [callVolumeData, setCallVolumeData] = useState({
    totalCalls: 15234,
    totalDuration: 2185230,
    avgDuration: 143.5,
    callsPerDay: 508,
  })

  const [callsOverTimeData, setCallsOverTimeData] = useState([
    { date: "Week 1", calls: 3200 },
    { date: "Week 2", calls: 3450 },
    { date: "Week 3", calls: 4100 },
    { date: "Week 4", calls: 4484 },
  ])

  const [durationOverTimeData, setDurationOverTimeData] = useState([
    { date: "Week 1", duration: 145 },
    { date: "Week 2", duration: 148 },
    { date: "Week 3", duration: 141 },
    { date: "Week 4", duration: 143.5 },
  ])

  const [sourceBreakdown, setSourceBreakdown] = useState([
    { source: "Human Agent", calls: 5234, duration: 1120450, avgDuration: 214.2, percentage: 34.4 },
    { source: "Web Agent", calls: 6123, duration: 748320, avgDuration: 122.2, percentage: 40.2 },
    { source: "Simulation Agent", calls: 3877, duration: 316460, avgDuration: 81.6, percentage: 25.4 },
  ])

  const [sourceChartData, setSourceChartData] = useState([
    { source: "Human Agent", calls: 5234 },
    { source: "Web Agent", calls: 6123 },
    { source: "Simulation Agent", calls: 3877 },
  ])

  const [simulationData, setSimulationData] = useState({
    totalSimulated: 3877,
    completed: 3651,
    dropped: 226,
    completionRate: 94.2,
  })

  const [simulationTrendData, setSimulationTrendData] = useState([
    { date: "Week 1", completed: 850, dropped: 60 },
    { date: "Week 2", completed: 920, dropped: 55 },
    { date: "Week 3", completed: 950, dropped: 50 },
    { date: "Week 4", completed: 931, dropped: 61 },
  ])

  const [evalData, setEvalData] = useState({
    totalEvaluated: 2856,
    passRate: 87.3,
    failRate: 12.7,
    avgScore: 8.1,
  })

  const [evalTrendData, setEvalTrendData] = useState([
    { date: "Week 1", passRate: 89.5 },
    { date: "Week 2", passRate: 88.2 },
    { date: "Week 3", passRate: 86.1 },
    { date: "Week 4", passRate: 87.3 },
  ])

  useEffect(() => {
    console.log("[v0] Filters changed - updating analytics data")

    // Simulate dynamic data update based on filters
    let multiplier = 1

    if (dateRange === "7d") multiplier = 0.25
    else if (dateRange === "90d") multiplier = 3

    if (selectedSource === "Human Agent") multiplier *= 0.34
    else if (selectedSource === "Web Agent") multiplier *= 0.4
    else if (selectedSource === "Simulation Agent") multiplier *= 0.25

    if (!includeSimulationCalls) multiplier *= 0.75

    const baseInputTokens = 2340120
    const baseOutputTokens = 2240114
    const baseCalls = 15234

    const newInputTokens = Math.round(baseInputTokens * multiplier)
    const newOutputTokens = Math.round(baseOutputTokens * multiplier)
    const newTotalTokens = newInputTokens + newOutputTokens

    setUsageData({
      totalInputTokens: newInputTokens,
      totalOutputTokens: newOutputTokens,
      totalTokens: newTotalTokens,
      totalCost: (newTotalTokens / 1000) * Number.parseFloat(costPer1kTokens),
    })

    const newTotalCalls = Math.round(baseCalls * multiplier)
    setCallVolumeData({
      totalCalls: newTotalCalls,
      totalDuration: Math.round(2185230 * multiplier),
      avgDuration: 143.5,
      callsPerDay: Math.round(newTotalCalls / (dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : 90)),
    })

    // Update chart data
    if (selectedSource === "all") {
      setSourceChartData([
        { source: "Human Agent", calls: Math.round(5234 * multiplier) },
        { source: "Web Agent", calls: Math.round(6123 * multiplier) },
        { source: "Simulation Agent", calls: Math.round(3877 * multiplier) },
      ])
    } else {
      setSourceChartData([{ source: selectedSource, calls: Math.round(baseCalls * multiplier) }])
    }
  }, [
    dateRange,
    selectedModel,
    selectedSource,
    selectedOwner,
    selectedCallStatus,
    costPer1kTokens,
    includeSimulationCalls,
  ])

  const showSimulationSection = selectedSource === "Simulation Agent" || selectedSource === "all"

  return (
    <div className="w-full h-full overflow-auto bg-white">
      <div className="max-w-[1600px] mx-auto p-8 space-y-8">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-black">Real-time Analytics</h1>
          </div>

          <div className="sticky top-0 z-10 bg-white pb-4 space-y-3 border-b border-gray-200">
            <div className="grid grid-cols-5 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Date Range</Label>
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="h-9 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Last 7 days</SelectItem>
                    <SelectItem value="30d">Last 30 days</SelectItem>
                    <SelectItem value="90d">Last 90 days</SelectItem>
                    <SelectItem value="custom">Custom range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Model</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="h-9 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Models</SelectItem>
                    <SelectItem value="gpt-4o-realtime">gpt-4o-realtime</SelectItem>
                    <SelectItem value="gpt-4o-mini-realtime">gpt-4o-mini-realtime</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Source</Label>
                <Select value={selectedSource} onValueChange={setSelectedSource}>
                  <SelectTrigger className="h-9 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    <SelectItem value="Human Agent">Human Agent</SelectItem>
                    <SelectItem value="Web Agent">Web Agent</SelectItem>
                    <SelectItem value="Simulation Agent">Simulation Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Owner</Label>
                <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                  <SelectTrigger className="h-9 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    <SelectItem value="team-a">Team A</SelectItem>
                    <SelectItem value="team-b">Team B</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-gray-600">Call Status</Label>
                <Select value={selectedCallStatus} onValueChange={setSelectedCallStatus}>
                  <SelectTrigger className="h-9 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="dropped">Dropped</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Usage & Cost Calculator */}
        <div className="border border-gray-300 rounded-lg p-6 bg-white">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-black">Usage & Cost Calculator</h2>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="include-simulation"
                  checked={includeSimulationCalls}
                  onCheckedChange={(checked) => setIncludeSimulationCalls(checked as boolean)}
                />
                <Label htmlFor="include-simulation" className="text-sm text-gray-600 cursor-pointer">
                  Include Simulation Calls
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Cost per 1K tokens:</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={costPer1kTokens}
                  onChange={(e) => setCostPer1kTokens(e.target.value)}
                  className="w-24 h-8 bg-white border-gray-300"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-2">Total Input Tokens</div>
              <div className="text-2xl font-semibold text-black">{usageData.totalInputTokens.toLocaleString()}</div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-2">Total Output Tokens</div>
              <div className="text-2xl font-semibold text-black">{usageData.totalOutputTokens.toLocaleString()}</div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-sm text-gray-600 mb-2">Total Tokens</div>
              <div className="text-2xl font-semibold text-black">{usageData.totalTokens.toLocaleString()}</div>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 bg-blue-50 border-blue-200">
              <div className="text-sm text-blue-700 mb-2 flex items-center gap-1">
                <DollarSign className="h-4 w-4" />
                Total Cost
              </div>
              <div className="text-2xl font-semibold text-blue-900">${usageData.totalCost.toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Call Volume & Duration */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-black">Call Volume & Duration</h2>

          <div className="grid grid-cols-4 gap-4">
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Total Calls</div>
                <Activity className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">{callVolumeData.totalCalls.toLocaleString()}</div>
              <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>+12.5%</span>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Total Call Duration</div>
                <Clock className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">
                {Math.floor(callVolumeData.totalDuration / 3600)}h{" "}
                {Math.floor((callVolumeData.totalDuration % 3600) / 60)}m
              </div>
              <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>+8.3%</span>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Avg Call Duration</div>
                <Clock className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">{callVolumeData.avgDuration}s</div>
              <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                <TrendingDown className="h-3 w-3" />
                <span>-3.1%</span>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Calls per Day</div>
                <Activity className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">{callVolumeData.callsPerDay}</div>
              <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>+5.2%</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-300 rounded-lg p-6 bg-white">
              <h3 className="text-base font-semibold text-black mb-4">Calls over Time</h3>
              <ResponsiveContainer width="100%" height={256}>
                <LineChart data={callsOverTimeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} dot={{ fill: "#3b82f6" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-gray-300 rounded-lg p-6 bg-white">
              <h3 className="text-base font-semibold text-black mb-4">Average Duration over Time</h3>
              <ResponsiveContainer width="100%" height={256}>
                <LineChart data={durationOverTimeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="duration" stroke="#10b981" strokeWidth={2} dot={{ fill: "#10b981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Source-wise Breakdown */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-black">Source-wise Breakdown</h2>

          <div className="grid grid-cols-2 gap-4">
            <div className="border border-gray-300 rounded-lg bg-white overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-300">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Source</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Calls</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">
                        Total Duration
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">Avg Duration</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {sourceBreakdown.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-black">{item.source}</td>
                        <td className="px-4 py-3 text-sm text-right text-black">{item.calls.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">
                          {Math.floor(item.duration / 60)}m
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{item.avgDuration}s</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-600">{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-6 bg-white">
              <h3 className="text-base font-semibold text-black mb-4">Calls by Source</h3>
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={sourceChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="source" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <Tooltip />
                  <Bar dataKey="calls" fill="#8b5cf6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Simulation Analytics */}
        {showSimulationSection && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-black">Simulation Analytics</h2>

            <div className="grid grid-cols-4 gap-4">
              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Total Simulated Calls</div>
                  <Activity className="h-4 w-4 text-gray-500" />
                </div>
                <div className="text-2xl font-semibold text-black">
                  {simulationData.totalSimulated.toLocaleString()}
                </div>
              </div>

              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Completed Simulations</div>
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                </div>
                <div className="text-2xl font-semibold text-green-600">{simulationData.completed.toLocaleString()}</div>
              </div>

              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Dropped Simulations</div>
                  <XCircle className="h-4 w-4 text-red-500" />
                </div>
                <div className="text-2xl font-semibold text-red-600">{simulationData.dropped.toLocaleString()}</div>
              </div>

              <div className="border border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm text-gray-600">Completion Rate</div>
                  <BarChart3 className="h-4 w-4 text-gray-500" />
                </div>
                <div className="text-2xl font-semibold text-black">{simulationData.completionRate}%</div>
                <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                  <TrendingUp className="h-3 w-3" />
                  <span>+2.1%</span>
                </div>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-6 bg-white">
              <h3 className="text-base font-semibold text-black mb-4">Simulation Completion Trend</h3>
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={simulationTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" fill="#10b981" name="Completed" />
                  <Bar dataKey="dropped" fill="#ef4444" name="Dropped" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Evals - Overall Quality */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-black">Evals – Overall Quality</h2>

          <div className="grid grid-cols-4 gap-4">
            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Total Evaluated Calls</div>
                <Activity className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">{evalData.totalEvaluated.toLocaleString()}</div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Pass Rate</div>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              </div>
              <div className="text-2xl font-semibold text-green-600">{evalData.passRate}%</div>
              <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                <TrendingDown className="h-3 w-3" />
                <span>-4.2%</span>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Fail Rate</div>
                <XCircle className="h-4 w-4 text-red-500" />
              </div>
              <div className="text-2xl font-semibold text-red-600">{evalData.failRate}%</div>
              <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                <TrendingUp className="h-3 w-3" />
                <span>+4.2%</span>
              </div>
            </div>

            <div className="border border-gray-300 rounded-lg p-4 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-600">Average Eval Score</div>
                <BarChart3 className="h-4 w-4 text-gray-500" />
              </div>
              <div className="text-2xl font-semibold text-black">{evalData.avgScore}/10</div>
              <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
                <TrendingDown className="h-3 w-3" />
                <span>-0.3</span>
              </div>
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-6 bg-white">
            <h3 className="text-base font-semibold text-black mb-4">Quality Trend (Pass Rate %)</h3>
            <ResponsiveContainer width="100%" height={256}>
              <LineChart data={evalTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: "12px" }} />
                <YAxis stroke="#6b7280" style={{ fontSize: "12px" }} domain={[80, 95]} />
                <Tooltip />
                <Line type="monotone" dataKey="passRate" stroke="#f59e0b" strokeWidth={2} dot={{ fill: "#f59e0b" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

